import React from 'react';

import { AFFECTEDFILES } from '../../../constants/applicationConstants';
import { listAffectedFiles } from '../Services/AffectedFilesService';
import { normalizeAffectedFile } from '../Components/AffectedFilesCatalog/affectedFilesCatalogUtils';

const FETCH_BATCH_SIZE = 100;
const DEFAULT_SORT = [{ field: 'commit_date', dir: 'desc' }];
const GENERAL_SEARCH_COLUMNS = [
  'bug_id',
  'file_path',
  'repo_name',
  'author',
  'action',
];
const SUPPORTED_SEARCH_FIELDS = new Set([
  'bug',
  'file',
  'path',
  'repo',
  'author',
]);
const EXPRESSION_SERVER_FIELD_MAP = {
  bug: 'bug_id',
  file: 'file_path',
  path: 'file_path',
  repo: 'repo_name',
  author: 'author',
};
const normalizeBugSummaryId = (value) => String(value ?? '').trim();
const normalizeSearchTerm = (value) => String(value ?? '').trim();

const parseSearchExpression = (token) => {
  const tokenValue = String(token ?? '').trim();
  const separatorIndex = tokenValue.indexOf(':');

  if (separatorIndex <= 0) {
    return null;
  }

  const field = tokenValue.slice(0, separatorIndex).trim().toLowerCase();
  const value = tokenValue.slice(separatorIndex + 1).trim();

  if (!SUPPORTED_SEARCH_FIELDS.has(field) || !value) {
    return null;
  }

  return {
    field,
    value,
  };
};

const parseSearchQuery = (searchTerm) => {
  const normalizedQuery = normalizeSearchTerm(searchTerm);

  if (!normalizedQuery) {
    return {
      normalizedQuery: '',
      expressions: [],
      mode: 'empty',
    };
  }

  const tokens = normalizedQuery.split(/\s+/);
  const expressions = tokens.map(parseSearchExpression);
  const isAdvancedQuery =
    expressions.length > 0 && expressions.every(Boolean);

  return {
    normalizedQuery,
    expressions: isAdvancedQuery ? expressions : [],
    mode: isAdvancedQuery ? 'advanced' : 'general',
  };
};

const normalizeBugSummaryCount = (value, fallback = 0) => {
  const normalized = Number(value);
  return Number.isFinite(normalized) ? normalized : fallback;
};

const normalizeBugSummary = (summary) => {
  const rawBugSummaries =
    summary?.bugSummaries ?? summary?.bugs ?? summary?.items ?? [];
  const bugSummaries = Array.isArray(rawBugSummaries)
    ? rawBugSummaries
        .map((item) => {
          const bugId = normalizeBugSummaryId(item?.bug_id ?? item?.bugId);

          if (!bugId) {
            return null;
          }

          return {
            bugId,
            bugUrl: String(item?.bug_url ?? item?.bugUrl ?? '').trim(),
            matchedFileCount: normalizeBugSummaryCount(
              item?.matchedFileCount ?? item?.matched_file_count ?? item?.fileCount,
              0
            ),
            deletionCount: normalizeBugSummaryCount(
              item?.deletionCount ?? item?.deletion_count,
              0
            ),
          };
        })
        .filter(Boolean)
    : [];
  const totalBugCount = normalizeBugSummaryCount(
    summary?.totalBugCount ?? summary?.totalBugs,
    bugSummaries.length
  );

  return {
    totalBugCount,
    bugSummaries,
  };
};

const mergeUniqueAffectedFiles = (currentFiles, nextFiles) => {
  const merged = new Map();

  currentFiles.forEach((file) => {
    merged.set(String(file.key), file);
  });

  nextFiles.forEach((file) => {
    merged.set(String(file.key), file);
  });

  return Array.from(merged.values());
};

const buildLegacyServerSearchFilters = (searchValue) => {
  const normalizedQuery = normalizeSearchTerm(searchValue);

  if (!normalizedQuery) {
    return [];
  }

  return GENERAL_SEARCH_COLUMNS.map((columnName, index) => ({
    ColumnName: columnName,
    Op: 'contains',
    ColumnValue: normalizedQuery,
    ...(index > 0 ? { Join: 'or' } : {}),
  }));
};

const buildExpressionServerFilters = (
  expressions = [],
  initialJoin
) =>
  expressions
    .map((expression, index) => {
      const columnName = EXPRESSION_SERVER_FIELD_MAP[expression.field];
      const columnValue = normalizeSearchTerm(expression.value);

      if (!columnName || !columnValue) {
        return null;
      }

      return {
        ColumnName: columnName,
        Op: 'contains',
        ColumnValue: columnValue,
        ...(initialJoin && index === 0
          ? { Join: initialJoin }
          : index > 0
            ? { Join: 'and' }
            : {}),
      };
    })
    .filter(Boolean);

const buildServerSearchFilters = (searchQueryInput) => {
  const searchQuery =
    typeof searchQueryInput === 'string'
      ? parseSearchQuery(searchQueryInput)
      : searchQueryInput;

  if (!searchQuery?.normalizedQuery) {
    return [];
  }

  if (searchQuery.mode !== 'advanced') {
    return buildLegacyServerSearchFilters(searchQuery.normalizedQuery);
  }

  return buildExpressionServerFilters(searchQuery.expressions);
};

const buildBugScopedFilters = ({ bugId, searchQuery } = {}) => {
  const normalizedBugId = normalizeBugSummaryId(bugId);

  if (!normalizedBugId) {
    return buildServerSearchFilters(searchQuery);
  }

  const filters = [
    {
      ColumnName: 'bug_id',
      Op: 'eq',
      ColumnValue: normalizedBugId,
    },
  ];

  if (searchQuery?.mode === 'advanced') {
    filters.push(
      ...buildExpressionServerFilters(
        searchQuery.expressions,
        'and'
      )
    );

    return filters;
  }

  const normalizedQuery = normalizeSearchTerm(searchQuery?.normalizedQuery);

  if (!normalizedQuery) {
    return filters;
  }

  return GENERAL_SEARCH_COLUMNS.flatMap((columnName, index) => [
    {
      ColumnName: 'bug_id',
      Op: 'eq',
      ColumnValue: normalizedBugId,
      ...(index > 0 ? { Join: 'or' } : {}),
    },
    {
      ColumnName: columnName,
      Op: 'contains',
      ColumnValue: normalizedQuery,
      Join: 'and',
    },
  ]);
};

const countFilesForBug = (files, bugId) => {
  const normalizedBugId = normalizeBugSummaryId(bugId);

  if (!normalizedBugId) {
    return 0;
  }

  return files.reduce(
    (total, file) =>
      total + (normalizeBugSummaryId(file?.bugId) === normalizedBugId ? 1 : 0),
    0
  );
};

const getBugPageForLoadedCount = (loadedCount) => {
  const normalizedLoadedCount = Number(loadedCount);
  const safeLoadedCount = Number.isFinite(normalizedLoadedCount)
    ? Math.max(0, normalizedLoadedCount)
    : 0;

  return Math.max(1, Math.floor(safeLoadedCount / FETCH_BATCH_SIZE) + 1);
};

const fetchAffectedFilesPage = async ({
  page = 1,
  searchTerm = '',
  filter,
  includeBugSummary = true,
  signal,
} = {}) => {
  const response = await listAffectedFiles({
    limit: FETCH_BATCH_SIZE,
    offset: page,
    filter: Array.isArray(filter) ? filter : buildServerSearchFilters(searchTerm),
    sort: DEFAULT_SORT,
    includeBugSummary,
    signal,
    moduleName: AFFECTEDFILES,
  });

  if (response?.isSuccess === false && !response?.affectedfiles) {
    throw new Error(
      response?.errorMessage || response?.message || 'Failed to load affected files.'
    );
  }

  const items = Array.isArray(response?.affectedfiles)
    ? response.affectedfiles.map(normalizeAffectedFile)
    : [];
  const totalFromApi = Number(response?.TotalCount ?? items.length);
  const total = Number.isFinite(totalFromApi) ? totalFromApi : items.length;

  return {
    items,
    total,
    bugSummary: includeBugSummary
      ? normalizeBugSummary(response?.summary)
      : {
          totalBugCount: 0,
          bugSummaries: [],
        },
  };
};

const useAffectedFilesCatalog = ({ searchTerm = '' } = {}) => {
  const [affectedFiles, setAffectedFiles] = React.useState([]);
  const [bugSummary, setBugSummary] = React.useState({
    totalBugCount: 0,
    bugSummaries: [],
  });
  const [errorMessage, setErrorMessage] = React.useState('');
  const [hasMore, setHasMore] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [nextPage, setNextPage] = React.useState(2);
  const [refreshing, setRefreshing] = React.useState(false);
  const [reportedTotal, setReportedTotal] = React.useState(0);
  const affectedFilesRef = React.useRef([]);
  const hasLoadedRef = React.useRef(false);
  const requestIdRef = React.useRef(0);
  const activeControllerRef = React.useRef(null);
  const searchQuery = React.useMemo(
    () => parseSearchQuery(searchTerm),
    [searchTerm]
  );
  const serverSearchFilters = React.useMemo(
    () => buildServerSearchFilters(searchQuery),
    [searchQuery]
  );

  const mergeAffectedFilesIntoState = React.useCallback(
    (nextItems) => {
      const updatedFiles = mergeUniqueAffectedFiles(
        affectedFilesRef.current,
        nextItems
      );

      affectedFilesRef.current = updatedFiles;
      setAffectedFiles(updatedFiles);
      setHasMore((currentHasMore) =>
        reportedTotal > 0 ? updatedFiles.length < reportedTotal : currentHasMore
      );

      return updatedFiles;
    },
    [reportedTotal]
  );

  const loadPage = React.useCallback(
    async ({ page = 1, append = false, refresh = false } = {}) => {
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      activeControllerRef.current?.abort();
      const requestController = new AbortController();
      activeControllerRef.current = requestController;

      if (refresh) {
        setRefreshing(true);
      } else if (append) {
        setLoadingMore(true);
      } else if (hasLoadedRef.current) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setErrorMessage('');

      try {
        const response = await fetchAffectedFilesPage({
          page,
          searchTerm,
          filter: serverSearchFilters,
          signal: requestController.signal,
        });

        if (requestId !== requestIdRef.current) {
          return null;
        }

        const updatedFiles = append ? mergeAffectedFilesIntoState(response.items) : response.items;
        const updatedTotal = response.total || updatedFiles.length;
        const nextPageValue = page + 1;
        const hasMoreValue = updatedFiles.length < updatedTotal && response.items.length > 0;

        if (!append) {
          affectedFilesRef.current = updatedFiles;
          setAffectedFiles(updatedFiles);
        }
        setBugSummary(response.bugSummary);
        setReportedTotal(updatedTotal);
        setNextPage(nextPageValue);
        setHasMore(hasMoreValue);
        hasLoadedRef.current = true;

        return {
          affectedFiles: updatedFiles,
          reportedTotal: updatedTotal,
          nextPage: nextPageValue,
          hasMore: hasMoreValue,
        };
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return null;
        }

        if (error?.name === 'AbortError') {
          return null;
        }

        if (!append && !hasLoadedRef.current) {
          affectedFilesRef.current = [];
          setAffectedFiles([]);
          setBugSummary({
            totalBugCount: 0,
            bugSummaries: [],
          });
          setReportedTotal(0);
          setNextPage(2);
          setHasMore(false);
        }

        setErrorMessage(error?.message || 'Unable to load affected files.');
        return null;
      } finally {
        if (requestId === requestIdRef.current) {
          if (activeControllerRef.current === requestController) {
            activeControllerRef.current = null;
          }

          setLoading(false);
          setLoadingMore(false);
          setRefreshing(false);
        }
      }
    },
    [mergeAffectedFilesIntoState, searchTerm, serverSearchFilters]
  );

  React.useEffect(() => {
    loadPage({
      page: 1,
      append: false,
      refresh: false,
    });
  }, [loadPage]);

  React.useEffect(() => {
    affectedFilesRef.current = affectedFiles;
  }, [affectedFiles]);

  React.useEffect(
    () => () => {
      activeControllerRef.current?.abort();
    },
    []
  );

  const loadMoreAffectedFiles = React.useCallback(async () => {
    if (loading || loadingMore || refreshing || !hasMore) {
      return null;
    }

    return loadPage({
      page: nextPage,
      append: true,
    });
  }, [hasMore, loadPage, loading, loadingMore, nextPage, refreshing]);

  const refreshAffectedFiles = React.useCallback(
    () =>
      loadPage({
        page: 1,
        append: false,
        refresh: true,
      }),
    [loadPage]
  );

  const loadMoreFilesForBug = React.useCallback(
    async ({ bugId, loadedCount = 0 } = {}) => {
      if (!bugId || loading || loadingMore || refreshing) {
        return null;
      }

      setErrorMessage('');

      try {
        const bugFilters = buildBugScopedFilters({
          bugId,
          searchQuery,
        });
        const currentLoadedCount = countFilesForBug(affectedFilesRef.current, bugId);
        let page = getBugPageForLoadedCount(
          Math.max(Number(loadedCount) || 0, currentLoadedCount)
        );
        let lastResult = null;

        for (let attempt = 0; attempt < 2; attempt += 1) {
          const response = await fetchAffectedFilesPage({
            page,
            filter: bugFilters,
            includeBugSummary: false,
          });
          const previousCount = countFilesForBug(affectedFilesRef.current, bugId);
          const updatedFiles = mergeAffectedFilesIntoState(response.items);
          const updatedBugCount = countFilesForBug(updatedFiles, bugId);
          const addedCount = Math.max(0, updatedBugCount - previousCount);
          const hasMoreForBug =
            updatedBugCount < response.total && response.items.length > 0;

          lastResult = {
            addedCount,
            hasMore: hasMoreForBug,
            loadedCount: updatedBugCount,
            page,
            total: response.total,
          };

          if (addedCount > 0 || !hasMoreForBug) {
            return lastResult;
          }

          page += 1;
        }

        return lastResult;
      } catch (error) {
        if (error?.name === 'AbortError') {
          return null;
        }

        setErrorMessage(error?.message || 'Unable to load more files for this bug.');
        return null;
      }
    },
    [loading, loadingMore, mergeAffectedFilesIntoState, refreshing, searchQuery]
  );

  return {
    affectedFiles,
    bugSummary,
    errorMessage,
    hasMore,
    loading,
    loadingMore,
    nextPage,
    refreshAffectedFiles,
    refreshing,
    reportedTotal,
    loadMoreAffectedFiles,
    loadMoreFilesForBug,
  };
};

export default useAffectedFilesCatalog;
